import Close from "@mui/icons-material/Close";
import Delete from "@mui/icons-material/Delete";
import FilterAlt from "@mui/icons-material/FilterAlt";
import { Box, Chip, Grid, Typography } from "@mui/material";
import {
	getGridDateOperators,
	useGridApiRef
} from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
	arrayOperators, handleArrayFiltering, handleDateFiltering,
	handleUserFiltering, identifierColumnOperators, statusOperators
} from "../../../../helpers/datagridFiltering";
import {
	emptyGridRowSelectionModel, gridRowSelectionSize
} from "../../../../helpers/dataGridRowSelectionModel";
import {
	isColumnPinningEnabled, normalizePinnedColumns, type PinnedColumnsInput
} from "../../../../helpers/dataGridPinnedColumns";
import useDynamicContainerHeight from "../../../../hooks/useDynamicContainerHeight";
import {
	selectColumnVisibilityForGrid, setGridColumnVisibility
} from "../../../../redux/dataGridPrefsSlice";
import {CoAppDataGrid, CoAppDataGridContainer, CoAppDataGridToolbar} from "../../styled/global.styled";
import CoAppIconTextButton from "../inputs/CoAppIconTextButton";
import CoAppColumnsButton from "./CoAppColumnsButton";
import CoAppDataGridAllSelectedRow from "./CoAppDataGridAllSelectedRow";
import DataGridCustomNoRowsOverlay from "./DataGridCustomNoRowsOverlay";

const arrayColumns = ["roles", "permissions", "groups", "rules", "users", "plugins"];
const FLEX_FIELDS = ["name", "description", "ruleName", "email", "label"];
const dateColumns = ["lastPhoneHome", "lastLogUpload", "updatedAt", "lastHeartbeat", "createdAt"];

const datagridInitialState = {
	filter: {
		filterModel: {
			items: [],
			quickFilterExcludeHiddenColumns: true,
		},
	},
};

// RuleLibraryDataGrid / RuleTrashDataGrid reserve 72px so the external
// selection footer below the grid stays inside CoAppDataGridMain (overflow:hidden).
const SELECTION_FOOTER_BOTTOM_PADDING = 72;
const DEFAULT_BOTTOM_PADDING = 32;

export default function CoAppStandardDataGrid(props) {
	const {
		columns, rows,
		allowSelection = true, targetResource, parentResource = "",
		parentResourceName = "", bulkActionText = "Delete", selectionModel = emptyGridRowSelectionModel(),
		setSelectionModel, handleModalToggle, customSelectionRow = null,
		customSelectionChangeHandler = null, sortModel = [], handleSortModelChange,
		loading, idFilterSet = false, clearFilterHandler = null,
		gridKey, filterModel, onFilterModelChange, processRowUpdate = null,
		selectionFooter = null, apiRef: externalApiRef = null,
		filterButtonEl = null, visibleRowCount = undefined,
		checkboxSelectionVisibleOnly = true,
		pinnedColumns: pinnedColumnsInput = null,
	} = props;

	const normalizedPinnedColumns = normalizePinnedColumns(pinnedColumnsInput as PinnedColumnsInput);
	const columnPinningEnabled = isColumnPinningEnabled(pinnedColumnsInput as PinnedColumnsInput);

	const dispatch = useDispatch();
	const persistedColumnVisibility = useSelector(
		gridKey ? selectColumnVisibilityForGrid(gridKey) : () => ({})
	);
	const handleColumnVisibilityModelChange = (next) => {
		if (!gridKey) return;
		dispatch(setGridColumnVisibility({ gridKey, model: next }));
	};

	const { containerRef, containerHeight } = useDynamicContainerHeight(
		selectionFooter ? SELECTION_FOOTER_BOTTOM_PADDING : DEFAULT_BOTTOM_PADDING
	);

	const internalApiRef = useGridApiRef();
	const apiRef = externalApiRef ?? internalApiRef;

	const invisibleFields = ["id", "uuid"];
	const filterableColumns = useMemo(
		() => columns
			.filter((column) => !invisibleFields.includes(column.field))
			.map((column) => {
				const baseColumn = { ...column, resizable: true };
				if (FLEX_FIELDS.includes(column.field) && baseColumn.flex == null) {
					baseColumn.flex = 1;
					baseColumn.minWidth = baseColumn.minWidth ?? baseColumn.width ?? 180;
				}
				if (arrayColumns.includes(column.field)) {
					return {
						...baseColumn,
						getApplyQuickFilterFn: handleArrayFiltering,
						filterOperators: arrayOperators
					};
				} else if (dateColumns.includes(column.field)) {
					return {
						...baseColumn,
						getApplyQuickFilterFn: handleDateFiltering,
						filterOperators: getGridDateOperators(true)
					};
				} else if (column.field === "user") {
					return {
						...baseColumn,
						getApplyQuickFilterFn: handleUserFiltering,
						filterOperators: identifierColumnOperators
					};
				} else if (column.field === "status" || column.field === "active" || column.field === "killSwitchActive") {
					return {
						...baseColumn,
						filterOperators: statusOperators
					};
				} else {
					return baseColumn;
				}
			}),
		[columns],
	);

	const handleSelectionModelChange = (newSelectionModel) => {
		if (rows[0]?.isDummyRow) return;
		setSelectionModel?.(newSelectionModel);
	};

	const selectionCount = gridRowSelectionSize(selectionModel, rows);
	const dataRowCount = rows.filter((r) => !r?.isDummyRow).length;
	const effectiveRowCount = visibleRowCount ?? dataRowCount;
	const allDataRowsSelected = selectionCount > 0 && selectionCount === effectiveRowCount;

	function CustomToolbar() {
		return (
			<div style={{ width: "100%" }}>
				<CoAppDataGridToolbar container>
					{idFilterSet && clearFilterHandler ? (
						<Grid component="div" sx={{ display: "flex", alignItems: "center", ml: 1 }}>
							<Chip
								icon={<FilterAlt sx={{ fontSize: 14 }} />}
								label="Filter active"
								onDelete={clearFilterHandler}
								deleteIcon={<Close sx={{ fontSize: 14 }} />}
								size="small"
								sx={{
									background: "rgba(2, 213, 171, 0.10)",
									color: "rgba(13, 31, 48, 0.78)",
									border: "1px solid rgba(2, 213, 171, 0.32)",
									fontWeight: 600,
									"& .MuiChip-icon": { color: "#02D5AB" },
									"& .MuiChip-deleteIcon": {
										color: "rgba(13, 31, 48, 0.55)",
										"&:hover": { color: "#0D1F30" },
									},
								}}
							/>
						</Grid>
					) : null}
					{
						selectionCount > 0 && targetResource !== "workstations" ?
							<Grid component="div">
								<CoAppIconTextButton
									icon={<Delete sx={{ fontSize: "1rem", marginRight: "5px", alignSelf: "baseline", marginTop: "2px" }} />}
									text={
										<Typography color="text.error" variant="body2" align="left" sx={{ width: "450px" }}>
											{`${bulkActionText} selected ${selectionCount > 1 ? targetResource : targetResource.substring(0, targetResource.length - 1)} ${selectionCount > 1 ? `(${selectionCount})` : ""} ${parentResource && parentResourceName ? ` from ${parentResourceName} ${parentResource}` : targetResource === "workstations" ? "kill switch" : ""}`}
										</Typography>
									}
									isDestructive={true}
									onClick={() => handleModalToggle()}
								/>
							</Grid>
							:
							null

					}
					{gridKey ? (
						<Grid component="div" sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
							<CoAppColumnsButton
								gridKey={gridKey}
								columns={filterableColumns}
								resourceCount={
									loading ? undefined : { count: effectiveRowCount, label: targetResource }
								}
							/>
						</Grid>
					) : null}
					{/* Pagination removed — DataGridPro virtualizes rows so
					    users scroll the body instead of paging. The Columns
					    chip already shows the row count. */}
					{
						customSelectionRow
							?
							<>
								{customSelectionRow}
							</>
							:
							<CoAppDataGridAllSelectedRow
								count={selectionCount}
								selectedResource={targetResource}
								parentResource={parentResource}
								parentResourceName={parentResourceName}
								onClickHandler={() => handleSelectionModelChange(emptyGridRowSelectionModel())}
								shouldShow={allDataRowsSelected && targetResource !== "workstations"}
							/>

					}
				</CoAppDataGridToolbar >
			</div >
		);
	}

	return (
		<>
		<CoAppDataGridContainer ref={containerRef} height={containerHeight}>
			<CoAppDataGrid
				apiRef={apiRef}
				loading={loading}
				columnHeaderHeight={56}
				// Friendlier sort labels in the column menu ("Ascending" /
				// "Descending" instead of MUI's default "Sort by ASC/DESC").
				localeText={{
					columnMenuSortAsc: "Ascending",
					columnMenuSortDesc: "Descending",
				}}
				columns={filterableColumns}
				rows={rows}
				isRowSelectable={(params) => params.row.isCoAppSuperAdmin !== undefined ? !params.row.isCoAppSuperAdmin : true}
				disableRowSelectionOnClick
				getRowId={(row) => row["id"] ? row["id"] : row["uuid"]}
				checkboxSelection={allowSelection}
				checkboxSelectionVisibleOnly={checkboxSelectionVisibleOnly}
				// Re-enable pinning when this grid declares pinned columns (theme sets
				// disableColumnPinning: true globally). Matches RuleLibraryDataGrid so
				// pinned columns stay fixed while the rest scroll horizontally.
				disableColumnPinning={!columnPinningEnabled}
				initialState={{
					...datagridInitialState,
					...(columnPinningEnabled && normalizedPinnedColumns
						? { pinnedColumns: normalizedPinnedColumns }
						: {}),
				}}
				slots={{
					toolbar: CustomToolbar,
					noResultsOverlay: () => (
						<DataGridCustomNoRowsOverlay message={`No ${targetResource} found.`} />
					),
					noRowsOverlay: () => (
						<DataGridCustomNoRowsOverlay message={`No ${targetResource} found.`} />
					),
				}}
				slotProps={{
					panel: {
						target: filterButtonEl ?? undefined,
					},
					filterPanel: {
						filterFormProps: {
							// Customize inputs by passing props
							logicOperatorInputProps: {
								size: "small",
							},
							columnInputProps: {
								size: "small",
							},
							operatorInputProps: {
								size: "small",
							},
							valueInputProps: {
								InputComponentProps: {
									size: "small",
								},
							},
						},
						sx: {
							// Customize inputs using css selectors
							"& .MuiDataGrid-filterFormLogicOperatorInput": { mr: 2 },
							"& .MuiDataGrid-filterFormColumnInput": { mr: 2, width: 150 },
							"& .MuiDataGrid-filterFormOperatorInput": { mr: 2 },
							"& .MuiDataGrid-filterFormValueInput": { width: 200 },
						},
					},
					loadingOverlay: {
						variant: 'skeleton',
						noRowsVariant: 'skeleton',
					},
				}}
				hideFooter
				onRowSelectionModelChange={customSelectionChangeHandler ? customSelectionChangeHandler : handleSelectionModelChange}
				rowSelectionModel={selectionModel ?? emptyGridRowSelectionModel()}
				getRowClassName={(params) => Boolean(params.row?.killSwitchActive) ? "CustomKillSwitchRow--active" : ""}
				sortModel={sortModel}
				onSortModelChange={handleSortModelChange}
				filterModel={filterModel}
				onFilterModelChange={onFilterModelChange}
				columnVisibilityModel={persistedColumnVisibility}
				onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
				processRowUpdate={processRowUpdate}
			/>
		</CoAppDataGridContainer>
		{selectionFooter}
		</>
	);
}
